/**
 * SFCC-11906 — fires the Constructor.io `item_detail_load` beacon on PDP.
 *
 * Renders null; side effect only. Mounted inside the lifted
 * `VariantSelector.SelectedVariant` render prop alongside `SegmentViewItemTracker`
 * so the selected variant id is available for `variationId`. Fires once per
 * viewed product (keyed on the loaded `productId` via a ref) — a variant switch
 * or quantity change on the same PDP does not re-fire, matching the
 * page-load semantics of `item_detail_load`.
 *
 * The CIO client is the shared `root.tsx` singleton (`useCioClient()`), null
 * during SSR / before hydration — guarded.
 *
 * SFCC-11961: with the beacon → SDK cutover switch on, the tracking sheet's
 * rules apply — re-fire when the shopper selects a variant, and fire for quick
 * view too (`quickView`, mounted by the quick view's `SelectedVariant`).
 */
import { useCioClient } from "~/utils/constructor";

import { useCioBeaconOnce } from "./cio-listing-beacons";
import { useCioSdkTrackingEnabled } from "./cio-sdk-tracking";

export const CioItemDetailTracker = ({
  productId,
  productName,
  variant,
  quickView = false,
}: {
  /** The loaded product id (the id space CIO browse/search hits use as `data.id`). */
  productId?: string;
  productName?: string;
  /** Selected variant, when the loaded product is a master/variant. */
  variant?: { productId: string } | undefined;
  /** Mounted in quick view — only tracked with SDK tracking on. */
  quickView?: boolean;
}) => {
  const cio = useCioClient();
  const sdkTrackingEnabled = useCioSdkTrackingEnabled();
  const variationId =
    variant?.productId && variant.productId !== productId
      ? variant.productId
      : undefined;

  // Beacon era: keyed on the loaded product id → fires once per viewed
  // product; a variant switch or quantity change does not re-fire.
  // SDK tracking: keyed on product + selected variant → re-fires per variant.
  const active = !!cio && !!productId && (!quickView || sdkTrackingEnabled);
  const key = active
    ? sdkTrackingEnabled
      ? `${productId}|${variationId ?? ""}`
      : (productId ?? null)
    : null;

  useCioBeaconOnce(key, () => {
    if (!cio || !productId) return;
    cio.tracker.trackItemDetailLoad({
      itemId: productId,
      itemName: productName ?? "",
      url: window.location.href,
      variationId,
    });
  });

  return null;
};
